import { useState, useEffect } from "react";
import { Trash2, Instagram, Plus } from "lucide-react";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";

const AdminInstaFeed = () => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ image_url: "", caption: "", link: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/insta-photos");
      setPhotos(r.data);
    } catch { toast.error("Failed to load photos"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const addPhoto = async (e) => {
    e.preventDefault();
    if (!form.image_url.trim()) return toast.error("Image URL is required");
    setSubmitting(true);
    try {
      await adminApi.post("/admin/insta-photos", { ...form, order: photos.length });
      setForm({ image_url: "", caption: "", link: "" });
      toast.success("Photo added");
      load();
    } catch { toast.error("Failed to add photo"); }
    finally { setSubmitting(false); }
  };

  const deletePhoto = async (id) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await adminApi.delete(`/admin/insta-photos/${id}`);
      setPhotos((p) => p.filter((x) => x.id !== id));
      toast.success("Photo deleted");
    } catch { toast.error("Failed to delete"); }
  };

  const seedDefault = async () => {
    try {
      const r = await adminApi.post("/admin/seed-insta-photos");
      toast.success(r.data.message);
      load();
    } catch { toast.error("Failed to seed"); }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-insta-page">
        <h1 className="text-3xl font-serif mb-2 flex items-center gap-2">
          <Instagram className="text-[#dc2743]" /> Instagram-style Photo Feed
        </h1>
        <p className="text-muted-foreground mb-6">
          Photos shown in the "@andamanavalon" section on the homepage. Paste an image URL (from your Instagram post,
          Cloudinary, or any hosted image) and optionally a caption + click-through link.
        </p>

        <form onSubmit={addPhoto} data-testid="add-insta-form" className="admin-card mb-6 grid grid-cols-1 md:grid-cols-4 gap-3">
          <Input
            data-testid="insta-image-url"
            placeholder="Image URL *"
            value={form.image_url}
            onChange={(e) => setForm({ ...form, image_url: e.target.value })}
            className="md:col-span-2"
            required
          />
          <Input
            data-testid="insta-caption"
            placeholder="Caption (optional)"
            value={form.caption}
            onChange={(e) => setForm({ ...form, caption: e.target.value })}
          />
          <Button type="submit" disabled={submitting} data-testid="add-insta-btn" className="gap-2">
            <Plus size={16} /> {submitting ? "Adding..." : "Add photo"}
          </Button>
          <Input
            data-testid="insta-link"
            placeholder="Click-through URL (optional, e.g. Instagram post URL)"
            value={form.link}
            onChange={(e) => setForm({ ...form, link: e.target.value })}
            className="md:col-span-4"
          />
        </form>

        {photos.length === 0 && !loading && (
          <div className="bg-white rounded-lg p-8 text-center" data-testid="empty-insta">
            <Instagram size={40} className="mx-auto text-muted-foreground/40 mb-3" />
            <p className="text-muted-foreground mb-4">No photos yet. Add your first one above or seed defaults.</p>
            <Button variant="outline" onClick={seedDefault} data-testid="seed-defaults-btn">
              Seed 8 sample photos
            </Button>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner"></div></div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="insta-grid">
            {photos.map((p) => (
              <div key={p.id} data-testid={`insta-item-${p.id}`} className="relative group aspect-square rounded-lg overflow-hidden bg-muted">
                <img src={p.image_url} alt={p.caption || "photo"} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-3">
                  <div className="text-white text-xs line-clamp-3">{p.caption || "—"}</div>
                  <button
                    onClick={() => deletePhoto(p.id)}
                    data-testid={`delete-insta-${p.id}`}
                    className="self-end bg-red-600 hover:bg-red-700 text-white p-2 rounded-full"
                    aria-label="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminInstaFeed;
